import { Box } from '@chakra-ui/react';
import * as React from 'react';

import { useIsMobile } from '@/imports/ui/responsive/Responsive';
import type { UtilitiesSlotAnchor } from '@/lib/webflow-poc/webflow-utilities-slot';
import {
  useWebflowUtilitiesLifecycle,
  WebflowUtilitiesLifecycleProvider,
} from '@/lib/webflow-poc/webflow-utilities-lifecycle';
import { WebflowNavUtilityCluster } from '@/lib/webflow-poc/WebflowNavUtilityCluster';

type WebflowUtilitiesOverlayProps = {
  anchor: UtilitiesSlotAnchor | null;
  /** Extra attrs for debugging / static vs dynamic portals. */
  dataAttribute?: string;
  lineHeight?: string | number;
};

/**
 * Stable tree under LifecycleProvider — hold state must NOT live on a parent
 * that remounts this provider (that wiped GlobalSearch before the modal opened).
 */
const WebflowUtilitiesOverlayInner: React.FC<WebflowUtilitiesOverlayProps> = ({
  anchor,
  dataAttribute = 'data-tradewing-nav-utilities',
  lineHeight,
}) => {
  const lifecycle = useWebflowUtilitiesLifecycle();
  const isMobile = useIsMobile();
  const isHeld = lifecycle?.isHeld ?? false;
  const isSearchHeld = lifecycle?.isSearchHeld ?? false;
  const lastAnchorRef = React.useRef<UtilitiesSlotAnchor | null>(null);
  const [hideIcons, setHideIcons] = React.useState(false);

  if (anchor) {
    lastAnchorRef.current = anchor;
  }

  // Keep last position while search/notifications are open and the slot collapses.
  const displayAnchor = anchor ?? (isHeld ? lastAnchorRef.current : null);

  // Only conceal icons on mobile Webflow while search is open (avoids covering
  // the modal). Never hide on desktop — that used pointer-events:none and
  // swallowed the opening click.
  React.useEffect(() => {
    if (!isSearchHeld || !isMobile) {
      setHideIcons(false);
      return;
    }
    const id = window.setTimeout(() => setHideIcons(true), 0);
    return () => window.clearTimeout(id);
  }, [isSearchHeld, isMobile]);

  if (!displayAnchor) {
    return null;
  }

  const iconsVisible = Boolean(anchor) && !hideIcons;

  const stopWebflowOutsideClose = (event: React.SyntheticEvent) => {
    // Portal sits outside .w-nav; stop so Webflow doesn't treat TW icon clicks
    // as "outside" and close the mobile menu.
    event.stopPropagation();
  };

  const dataAttrs = { [dataAttribute]: '' } as Record<string, string>;

  return (
    <Box
      position="fixed"
      top={`${displayAnchor.top}px`}
      left={`${displayAnchor.left}px`}
      w={`${displayAnchor.width}px`}
      h={`${displayAnchor.height}px`}
      // Above typical Webflow mobile nav overlays (~9999).
      zIndex={10050}
      display="flex"
      alignItems="center"
      justifyContent="flex-end"
      lineHeight={lineHeight}
      pointerEvents={iconsVisible ? 'auto' : 'none'}
      visibility={iconsVisible ? 'visible' : 'hidden'}
      isolation="isolate"
      {...dataAttrs}
      onMouseDown={stopWebflowOutsideClose}
      onClick={stopWebflowOutsideClose}
      onTouchStart={stopWebflowOutsideClose}
      onTouchEnd={stopWebflowOutsideClose}
    >
      <WebflowNavUtilityCluster />
    </Box>
  );
};

/**
 * Webflow-gated utilities overlay body. Provider stays mounted across holds.
 */
export const WebflowUtilitiesOverlay: React.FC<WebflowUtilitiesOverlayProps> = (
  props
) => (
  <WebflowUtilitiesLifecycleProvider>
    <WebflowUtilitiesOverlayInner {...props} />
  </WebflowUtilitiesLifecycleProvider>
);
